import "./tutorial.scss";

import { filter, uniq } from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormButton from "../../components/form/FormButton";
import FormCheckbox from "../../components/form/FormCheckbox";
import FormDropdown from "../../components/form/FormDropdown";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorGroup from "../../components/inspector/InspectorGroup";
import InspectorRow from "../../components/inspector/InspectorRow";
import Icon from "../../components/interface/Icon";
import PubSubTopics from "../../constants/pubSubTopics";
import { activeTutorialPathSelectors } from "../../modules/activeTutorialPath";
import { appStateOperations } from "../../modules/appState";
import { definitionSelectors } from "../../modules/definition";
import { narrativeSettingsSelectors } from "../../modules/definition/narrativeSettings";
import { typeSelectors } from "../../modules/definition/types";
import { syncSettingsSelectors } from "../../modules/sync/settings";
import { tutorialOperations, tutorialSelectors } from "../../modules/tutorial";
import { nativeCommsBroadcastMessageTypes } from "../../nativeComms";
import getActionToBeginTutorialPath from "../../tutorial/getActionToBeginTutorialPath";
import { tutorialDisplaySelectors } from "../../tutorial/selectors";
import tutorialPaths from "../../tutorial/tutorialPaths";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import openUrl from "../../utils/openUrl";
import preprocessorQuery from "../../utils/preprocessorQuery";
import TutorialGroupView from "./TutorialGroupView";

class TutorialPanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      expandedTips: []
    };

    this.renderTutorial = this.renderTutorial.bind(this);
    this.renderIntro = this.renderIntro.bind(this);
    this.renderSilenceCheckbox = this.renderSilenceCheckbox.bind(this);
    this.renderLinks = this.renderLinks.bind(this);
    this.renderLink = this.renderLink.bind(this);
    this.onSelectTutorial = this.onSelectTutorial.bind(this);
    this.onToggleTipDetails = this.onToggleTipDetails.bind(this);
  }

  onToggleTipDetails(tipId) {
    const wasExpanded = this.state.expandedTips.indexOf(tipId) >= 0;
    const expandedTips = wasExpanded
      ? filter(this.state.expandedTips, id => id !== tipId)
      : uniq([...this.state.expandedTips, tipId]);

    this.setState({
      expandedTips
    });
  }

  renderIntro() {
    if (!this.props.activeTutorialPath) {
      return (
        <InspectorGroup noBottomLine={true}>
          <div className="tutorialIntro">
            <div className="tutorialWelcome">Welcome to Aeon Timeline</div>
            <div className="tutorialStart">
              <FormButton
                label="Begin tutorial"
                onClick={() => this.props.onBeginTutorialPath(tutorialPaths.NEW_USER_NO_SYNC)}
                primary={true}
              />
            </div>
          </div>
        </InspectorGroup>
      );
    } else {
      return (
        <InspectorGroup noBottomLine={true}>
          <div className="tutorialIntro">
            <div className="tutorialWelcome">Welcome to Aeon Timeline</div>
            <div className="tutorialStart">
              <FormButton
                label="Begin tutorial"
                onClick={() => this.props.onBeginTutorial(this.props.groups.allIds.get(0))}
                primary={true}
              />
            </div>
          </div>
        </InspectorGroup>
      );
    }
  }

  onSelectTutorial(groupId) {
    this.props.onSelectTutorial(groupId);
  }

  renderTutorial() {
    const isMobile = preprocessorQuery.isMobile();
    const groupOptions = tutorialDisplaySelectors.getTutorialDropdownOptionsFromGroups(
      this.props.groups,
      this.props.sidebarTypes,
      this.props.orderedNarrativeTypes,
      this.props.typesById,
      this.props.defaultSequenceType,
      this.props.narrativeSectionTypes,
      this.props.syncAppName
    );

    return (
      <Fragment>
        <InspectorGroup noBottomLine={true}>
          <InspectorRow>
            <InspectorField>
              <FormDropdown
                options={groupOptions}
                value={this.props.visibleTutorial}
                onChange={this.onSelectTutorial}
                title="Tutorial"
                backText="Back"
                showDescription={true}
              />
            </InspectorField>
          </InspectorRow>
        </InspectorGroup>
        <InspectorGroup className={!isMobile ? "overflowScroll" : ""}>
          <TutorialGroupView
            selectedTutorial={this.props.visibleTutorial}
            expandedTips={this.state.expandedTips}
            onToggleTipDetails={this.onToggleTipDetails}
          />
          {this.renderLinks(true)}
        </InspectorGroup>
      </Fragment>
    );
  }

  renderSilenceCheckbox() {
    return (
      <InspectorGroup>
        <InspectorRow>
          <InspectorField>
            <FormCheckbox
              label="Mute tutorial"
              checkboxState={this.props.tutorialSilenced}
              onChange={val => {
                this.props.setTutorialSilenced(val);
              }}
              showHelpIcon={true}
              helpTooltip="Don't show the current tutorial step and stop all tutorial notifications"
            />
          </InspectorField>
        </InspectorRow>
      </InspectorGroup>
    );
  }

  renderLinks(noPadding) {
    let links = tutorialDisplaySelectors.getGroupLinks(this.props.visibleTutorial);
    if (!links) {
      links = [
        { label: "User Interface Overview", path: "https://help.timeline.app/article/141-user-interface-overview" },
        {
          label: "Glossary and Key Concepts",
          path: "https://help.timeline.app/article/154-glossary"
        },
        {
          label: "Knowledge Base",
          path: "https://help.timeline.app"
        }
      ];
    }

    return (
      <InspectorGroup label="Helpful Links" noBottomLine={true} style={noPadding ? { padding: 0 } : null}>
        <InspectorRow>
          <div className="tutorialLinks">{links.map(link => this.renderLink(link.label, link.path))}</div>
        </InspectorRow>
      </InspectorGroup>
    );
  }

  renderLink(label, path) {
    return (
      <div key={label} className="tutorialLink">
        <FormButton
          label={label}
          linkStyle={true}
          onClick={() => {
            openUrl(path);
          }}
        />
      </div>
    );
  }

  render() {
    const isIos = preprocessorQuery.isIos();

    return (
      <div className="inspector">
        <InspectorGroup>
          <div className="tutorialHeader">
            <Icon icon="toolbarTutorial" color="iconMono" />
            <span>Tutorial</span>
            {!isIos ? (
              <Icon icon="rightArrow" className="collapseArrow" color="iconMono" onClick={this.props.onCollapseClick} />
            ) : null}
          </div>
        </InspectorGroup>
        {this.props.activeTutorial ? this.renderTutorial(this.props) : this.renderIntro(this.props)}
        {this.renderSilenceCheckbox(this.props)}
        {this.props.activeTutorial ? null : this.renderLinks(false)}
      </div>
    );
  }
}

TutorialPanel.propTypes = {
  activeTutorialPath: PropTypes.string,
  activeTutorial: PropTypes.string,
  visibleTutorial: PropTypes.string,
  groups: PropTypes.object.isRequired,
  onSelectTutorial: PropTypes.func.isRequired,
  onBeginTutorial: PropTypes.func.isRequired,
  onBeginTutorialPath: PropTypes.func.isRequired,
  onCollapseClick: PropTypes.func.isRequired,
  tutorialSilenced: PropTypes.bool.isRequired,
  setTutorialSilenced: PropTypes.func.isRequired,
  completedTips: ImmutablePropTypes.set,
  defaultSequenceType: PropTypes.string.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  sidebarTypes: ImmutablePropTypes.list,
  orderedNarrativeTypes: ImmutablePropTypes.list,
  narrativeSectionTypes: ImmutablePropTypes.list,
  isNarrativeEnabled: PropTypes.bool.isRequired,
  syncAppName: PropTypes.string
};

const mapStateToProps = state => {
  const data = tutorialDisplaySelectors.getTutorialDisplayStatus(state.present);
  return {
    groups: data.groups,
    activeTutorialPath: activeTutorialPathSelectors.getActiveTutorialPath(state.present),
    activeTutorial: data.activeTutorial,
    visibleTutorial: data.visibleTutorial,
    tutorialSilenced: tutorialSelectors.getTutorialSilenced(state.present),
    completedTips: tutorialSelectors.getCompletedTips(state.present),
    defaultSequenceType: definitionSelectors.getDefaultSequenceType(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    sidebarTypes: typeSelectors.getSidebarTypes(state.present),
    orderedNarrativeTypes: typeSelectors.getOrderedNarrativeTypes(state.present),
    narrativeSectionTypes: narrativeSettingsSelectors.getNarrativeLabels(state.present),
    isNarrativeEnabled: definitionSelectors.isNarrativeAllowed(state.present),
    syncAppName: syncSettingsSelectors.getSyncApplicationName(state.present)
  };
};

const readonlyActions = {
  onSelectTutorial: tutorialOperations.setVisibleTutorial,
  onBeginTutorial: tutorialOperations.setActiveTutorial,
  onBeginTutorialPath: getActionToBeginTutorialPath,
  onCollapseClick: appStateOperations.hideRightPanel
};

const editingActions = {
  setTutorialSilenced: silenced => {
    PubSub.publish(PubSubTopics.sendNativeMessage, {
      msg: nativeCommsBroadcastMessageTypes.SILENCE_TUTORIAL,
      silenced: silenced
    });
    return tutorialOperations.setTutorialSilenced(silenced);
  }
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(TutorialPanel);
